import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const QuickActionLauncher = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [userRole, setUserRole] = useState('qa_manager'); // qa_manager, lab_tech, regulatory_officer
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef?.current && !menuRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getContextualActions = () => {
    const currentPath = location?.pathname;
    const baseActions = [];

    // Role-based actions
    const roleActions = {
      qa_manager: [
        {
          id: 'new_batch_test',
          label: 'New Batch Test',
          description: 'Initiate batch testing workflow',
          icon: 'FlaskConical',
          path: '/batch-testing-records',
          color: 'text-primary',
          bgColor: 'bg-primary/10'
        },
        {
          id: 'adverse_event',
          label: 'Report Adverse Event',
          description: 'Create new adverse event report',
          icon: 'Shield',
          path: '/adverse-event-reporting',
          color: 'text-warning',
          bgColor: 'bg-warning/10'
        },
        {
          id: 'equipment_check',
          label: 'Equipment Check',
          description: 'Schedule equipment calibration',
          icon: 'Settings',
          path: '/laboratory-equipment',
          color: 'text-secondary',
          bgColor: 'bg-secondary/10'
        }
      ],
      lab_tech: [
        {
          id: 'new_batch_test',
          label: 'New Batch Test',
          description: 'Start batch testing procedure',
          icon: 'FlaskConical',
          path: '/batch-testing-records',
          color: 'text-primary',
          bgColor: 'bg-primary/10'
        },
        {
          id: 'equipment_maintenance',
          label: 'Log Maintenance',
          description: 'Record equipment maintenance',
          icon: 'Wrench',
          path: '/laboratory-equipment',
          color: 'text-secondary',
          bgColor: 'bg-secondary/10'
        }
      ],
      regulatory_officer: [
        {
          id: 'compliance_review',
          label: 'Compliance Review',
          description: 'Start regulatory compliance review',
          icon: 'CheckCircle',
          path: '/regulatory-compliance',
          color: 'text-success',
          bgColor: 'bg-success/10'
        },
        {
          id: 'audit_trail',
          label: 'Audit Trail',
          description: 'Generate audit trail report',
          icon: 'Search',
          path: '/audit-trail-viewer',
          color: 'text-accent',
          bgColor: 'bg-accent/10'
        }
      ]
    };

    // Context-specific actions based on current page
    const contextActions = {
      '/quality-dashboard': [
        {
          id: 'quick_overview',
          label: 'Generate Report',
          description: 'Create quality overview report',
          icon: 'FileText',
          action: () => console.log('Generate report'),
          color: 'text-primary',
          bgColor: 'bg-primary/10'
        }
      ],
      '/batch-testing-records': [
        {
          id: 'bulk_import',
          label: 'Bulk Import',
          description: 'Import multiple test records',
          icon: 'Upload',
          action: () => console.log('Bulk import'),
          color: 'text-accent',
          bgColor: 'bg-accent/10'
        }
      ],
      '/adverse-event-reporting': [
        {
          id: 'urgent_report',
          label: 'Urgent Report',
          description: 'Create high-priority adverse event',
          icon: 'AlertTriangle',
          action: () => console.log('Urgent report'),
          color: 'text-error',
          bgColor: 'bg-error/10'
        }
      ]
    };

    // Combine role-based and context-specific actions
    const actions = [
      ...(roleActions?.[userRole] || []),
      ...(contextActions?.[currentPath] || [])
    ];

    // Remove duplicates and limit to 6 actions
    const uniqueActions = actions?.filter((action, index, self) => 
      index === self?.findIndex(a => a?.id === action?.id)
    )?.slice(0, 6);

    return uniqueActions;
  };

  const handleActionClick = (action) => {
    if (action?.path) {
      navigate(action?.path);
    } else if (action?.action) {
      action?.action();
    }
    setIsOpen(false);
  };

  const actions = getContextualActions();

  return (
    <div className={`fixed bottom-6 right-6 z-40 ${className}`} ref={menuRef}>
      {/* Action Menu */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-80 bg-popover border border-border rounded-lg shadow-modal animate-slide-up">
          <div className="p-4 border-b border-border">
            <h3 className="font-medium text-popover-foreground">Quick Actions</h3>
            <p className="text-sm text-muted-foreground">
              Context-aware workflow shortcuts
            </p>
          </div>
          
          <div className="p-2">
            {actions?.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground">
                <Icon name="Zap" size={24} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No quick actions available</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {actions?.map((action) => (
                  <button
                    key={action?.id}
                    onClick={() => handleActionClick(action)}
                    className={`p-3 rounded-lg border border-border hover:border-primary/20 transition-all duration-150 text-left group ${action?.bgColor} hover:scale-105`}
                  >
                    <div className="flex items-center space-x-2 mb-2">
                      <Icon 
                        name={action?.icon} 
                        size={16} 
                        className={`${action?.color} group-hover:scale-110 transition-transform duration-150`}
                      />
                      <span className="text-sm font-medium text-popover-foreground truncate">
                        {action?.label}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {action?.description}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-border">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Role: {userRole?.replace('_', ' ')?.toUpperCase()}</span>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-primary hover:text-primary/80 transition-colors duration-150"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Floating Action Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-modal hover:shadow-lg hover:scale-105 transition-all duration-150 flex items-center justify-center group ${
          isOpen ? 'rotate-45' : ''
        }`}
      >
        <Icon 
          name={isOpen ? 'X' : 'Zap'} 
          size={24} 
          className="group-hover:scale-110 transition-transform duration-150"
        />
      </button>
      {/* Keyboard Shortcut Hint */}
      {!isOpen && (
        <div className="absolute bottom-16 right-0 bg-popover border border-border rounded-md px-2 py-1 text-xs text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none">
          Press <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Ctrl+K</kbd> for shortcuts
        </div>
      )}
    </div>
  );
};

export default QuickActionLauncher;